import type { ReactNode } from 'react';
import { useEffect, useEffectEvent, useLayoutEffect, useRef } from 'react';

interface ModalProps {
  /** Called once the dialog has closed: Escape, a backdrop click, or `close()`. */
  onClose: () => void;
  /**
   * Escape and backdrop clicks, when they must not simply close (back out of a
   * sub-mode first, or stay open while a request is in flight). The browser's
   * own close is cancelled; this decides what happens.
   */
  onCancel?: () => void;
  /**
   * Extra classes of the `<dialog>`, which fills the screen (transparent) and
   * centres its content; the dimmed layer is styled with `backdrop:`.
   */
  className?: string;
  /** `alertdialog` for a confirmation that interrupts; `dialog` by default. */
  role?: 'alertdialog';
  'aria-label'?: string;
  'aria-labelledby'?: string;
  children: ReactNode;
}

/**
 * A modal on the native `<dialog>`, shown with `showModal()` for as long as it
 * is mounted — render it only while open. The browser then does what our
 * hand-made overlays did not, or only partly: top layer (no portal, no
 * z-index, out of `main > *`'s transform), the rest of the page inert, focus
 * kept inside, Escape, and focus handed back to the opener on close.
 *
 * Never render `open`: `showModal()` throws on a dialog that already has it.
 */
export function Modal({
  onClose,
  onCancel,
  className,
  role,
  children,
  ...aria
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  // A click closes on the backdrop only if the press started there too: a text
  // selection dragged out of the panel must not dismiss it.
  const pressedOnBackdropRef = useRef(false);
  // Set right before our own close() on unmount: that close is not the user's.
  const unmountingRef = useRef(false);

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return undefined;
    }
    unmountingRef.current = false;
    if (!dialog.open) {
      dialog.showModal();
    }
    // Unmounting closes it properly, which is what returns focus to the opener
    // (removing an open dialog from the DOM does not).
    return () => {
      unmountingRef.current = true;
      if (dialog.open) {
        dialog.close();
      }
    };
  }, []);

  // Escape is taken at keydown, before it becomes a `cancel`: Chrome stops
  // honouring preventDefault() on a `cancel` after two Escapes without a click
  // in between, and would close a dialog that has to stay open. On `document`,
  // not the dialog: a button disabled under the focus (Cancel while uploading)
  // drops it to <body>, and the browser still sends the dialog that `cancel`.
  const cancel = useEffectEvent(() => onCancel?.());
  const cancellable = onCancel !== undefined;
  useEffect(() => {
    if (!cancellable) {
      return undefined;
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape' || e.isComposing || e.defaultPrevented) {
        return;
      }
      e.preventDefault();
      cancel();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [cancellable]);

  return (
    <dialog
      ref={dialogRef}
      {...aria}
      role={role}
      // The dialog box is the whole screen: a click on the margin around the
      // panel is a click on the dialog itself, which is what closes it.
      className={`fixed inset-0 m-0 size-full max-h-none max-w-none items-center justify-center border-0 bg-transparent p-4 open:flex ${className ?? ''}`}
      // Other close requests (Android back): cancelled when the browser lets us.
      onCancel={(e) => {
        if (!onCancel) {
          return;
        }
        e.preventDefault();
        onCancel();
      }}
      onClose={() => {
        // Not ours (unmount), and not stale: StrictMode remounts and reopens
        // before the queued `close` of its first unmount fires.
        if (unmountingRef.current || dialogRef.current?.open !== false) {
          return;
        }
        onClose();
      }}
      onMouseDown={(e) => {
        pressedOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (!pressedOnBackdropRef.current || e.target !== e.currentTarget) {
          return;
        }
        // A light dismiss, so the same request as Escape.
        if (onCancel) {
          onCancel();
        } else {
          e.currentTarget.close();
        }
      }}
    >
      {children}
    </dialog>
  );
}
